import { Injectable } from '@angular/core';
import { SettingV2 } from 'api-client';
import { Observable, throwError, of } from 'rxjs';

import {
  PaymentCalculationRequest,
  PaymentCalculationResponse,
  PaymentMethodType,
} from '../models/payment-calculation.interface';

@Injectable({
  providedIn: 'root',
})
export class PaymentCalculationService {
  /**
   * Calculate payment according to backend logic
   */
  calculatePayment(
    request: PaymentCalculationRequest,
    settings: SettingV2,
  ): Observable<PaymentCalculationResponse> {
    try {
      // Validate request
      if (!this.isValidRequest(request)) {
        return throwError(() => new Error('Invalid payment calculation request'));
      }

      // Additional validation for payment methods
      if (
        (request.paymentMethod === PaymentMethodType.PIX ||
          request.paymentMethod === PaymentMethodType.DebitoAVista) &&
        request.installments !== 1
      ) {
        return throwError(
          () => new Error('PIX and Débito à Vista payments must have exactly 1 installment'),
        );
      }

      if (
        request.paymentMethod === PaymentMethodType.CreditoParcelado &&
        (request.installments < 1 || request.installments > 12)
      ) {
        return throwError(
          () => new Error('Credit card payments must have between 1 and 12 installments'),
        );
      }

      if (request.paymentMethod === PaymentMethodType.CreditoAVista && request.installments !== 1) {
        return throwError(
          () => new Error('Crédito à Vista payments must have exactly 1 installment'),
        );
      }

      // Validate currency code
      const supportedCurrencies = ['EUR', 'USD', 'BRL'];
      if (!supportedCurrencies.includes(request.currencyCode.toUpperCase())) {
        return throwError(
          () =>
            new Error(
              `Unsupported currency: ${request.currencyCode}. Supported currencies: ${supportedCurrencies.join(', ')}`,
            ),
        );
      }

      const processedSettings = this.processSettingsForCurrency(settings, request.currencyCode);

      // Initialize calculation variables
      let totalValueBRL: number;
      let processingFee: number;
      let antecipationFee: number;
      const installments = request.installments;

      if (request.paymentMethod === PaymentMethodType.DebitoAVista) {
        // Debit card processing
        processingFee = processedSettings.debitCardProcessmentFee!;
        antecipationFee = 0; // No anticipation for debit

        totalValueBRL =
          Math.round(
            request.baseAmount *
              (processedSettings.oficialExchangeRate! /
                (1 -
                  processedSettings.iof! -
                  processedSettings.spread! -
                  processingFee -
                  processedSettings.operacionalMargin!)) *
              100,
          ) / 100;
      } else if (
        request.paymentMethod === PaymentMethodType.CreditoAVista ||
        request.paymentMethod === PaymentMethodType.CreditoParcelado
      ) {
        // Credit card processing (unified logic for all installments)
        processingFee = this.getProcessingFee(request.installments, processedSettings);
        antecipationFee =
          processedSettings.antecipationFee! + processedSettings.financingFee! * (installments - 1);

        if (request.establishmentSupportsFinancingCosts) {
          // Establishment absorbs financing costs
          totalValueBRL =
            Math.round(
              request.baseAmount *
                (processedSettings.oficialExchangeRate! /
                  (1 -
                    processedSettings.iof! -
                    processedSettings.spread! -
                    processingFee -
                    processedSettings.operacionalMargin!)) *
                100,
            ) / 100;
        } else {
          // Customer pays financing costs
          totalValueBRL =
            Math.round(
              request.baseAmount *
                (processedSettings.oficialExchangeRate! /
                  (1 -
                    processedSettings.iof! -
                    processedSettings.spread! -
                    processingFee -
                    antecipationFee -
                    processedSettings.operacionalMargin!)) *
                100,
            ) / 100;
        }
      } else {
        // PIX
        processingFee = processedSettings.pixProcessmentFee!;
        antecipationFee = 0; // No anticipation for Pix

        // not including processingFee for Pix
        totalValueBRL =
          Math.round(
            request.baseAmount *
              (processedSettings.oficialExchangeRate! /
                (1 -
                  processedSettings.iof! -
                  processedSettings.spread! -
                  processedSettings.operacionalMargin!)) *
              100,
          ) / 100;
      }

      const totalValue =
        Math.round((totalValueBRL / processedSettings.oficialExchangeRate!) * 100) / 100;
      const installmentValueBRL = Math.round((totalValueBRL / installments) * 100) / 100;

      // Cost calculations
      const estimatedExchangeCostBRL =
        Math.round(
          request.baseAmount *
            (processedSettings.oficialExchangeRate! /
              (1 - processedSettings.iof! - processedSettings.spread!)) *
            100,
        ) / 100;
      const processingCostBRL = Math.round(totalValueBRL * processingFee * 100) / 100;
      const maxAntecipationCostBRL = Math.round(totalValueBRL * antecipationFee * 100) / 100;

      const response: PaymentCalculationResponse = {
        value: request.baseAmount,
        paymentMethod: request.paymentMethod,
        currencyCode: request.currencyCode.toUpperCase(),
        usedExchangeRate: processedSettings.oficialExchangeRate!,
        totalValue: totalValue,
        totalValueBRL: totalValueBRL,
        installments: installments,
        installmentValueBRL: installmentValueBRL,
        processingFee: processingFee,
        processingCostBRL: processingCostBRL,
        estimatedExchangeCostBRL: estimatedExchangeCostBRL,
        antecipationFee: antecipationFee,
        maxAntecipationCostBRL: maxAntecipationCostBRL,
      };

      return of(response);
    } catch (error) {
      return throwError(() => error);
    }
  }

  private isValidRequest(request: PaymentCalculationRequest): boolean {
    return !!(
      request &&
      request.baseAmount > 0 &&
      request.paymentMethod &&
      request.installments > 0 &&
      request.currencyCode &&
      typeof request.establishmentSupportsFinancingCosts === 'boolean'
    );
  }

  private processSettingsForCurrency(settings: SettingV2, currencyCode: string): SettingV2 {
    const upperCurrencyCode = currencyCode.toUpperCase();

    if (upperCurrencyCode === 'BRL') {
      return {
        ...settings,
        oficialExchangeRate: 1,
        iof: 0,
        spread: 0,
        operacionalMargin: 0.04,
        cardProcessmentFee: 0.0304,
        debitCardProcessmentFee: 0.0079,
        antecipationFee: 0.0115,
        financingFee: 0.005,
        twoMonthsFee: 0.0229,
        threeMonthsFee: 0.0229,
        fourMonthsFee: 0.0229,
        fiveMonthsFee: 0.0229,
        sixMonthsFee: 0.0229,
        sevenMonthsFee: 0.0224,
        eightMonthsFee: 0.0224,
        nineMonthsFee: 0.0224,
        tenMonthsFee: 0.0224,
        elevenMonthsFee: 0.0224,
        twelveMonthsFee: 0.0224,
      };
    }

    return settings;
  }

  private getProcessingFee(installments: number, settings: SettingV2): number {
    switch (installments) {
      case 1:
        return settings.cardProcessmentFee!;
      case 2:
        return settings.twoMonthsFee!;
      case 3:
        return settings.threeMonthsFee!;
      case 4:
        return settings.fourMonthsFee!;
      case 5:
        return settings.fiveMonthsFee!;
      case 6:
        return settings.sixMonthsFee!;
      case 7:
        return settings.sevenMonthsFee!;
      case 8:
        return settings.eightMonthsFee!;
      case 9:
        return settings.nineMonthsFee!;
      case 10:
        return settings.tenMonthsFee!;
      case 11:
        return settings.elevenMonthsFee!;
      case 12:
        return settings.twelveMonthsFee!;
      default:
        return settings.cardProcessmentFee!;
    }
  }

  /**
   * Get payment method type based on credit flag and installments
   */
  getPaymentMethodType(isCredit: boolean, _installments: number): PaymentMethodType {
    if (!isCredit) {
      return PaymentMethodType.PIX; // or DebitoAVista based on your logic
    }

    // Use CreditoParcelado for all credit card payments regardless of installments
    return PaymentMethodType.CreditoParcelado;
  }
}
